import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { AllocationService } from '../../services/allocation.service';
import { Allocation, AllocationCreditType } from '../../interfaces/allocation.interface';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';

interface Props {
  creditType: AllocationCreditType;
  creditId: string;
  /** Bump to force a reload after an allocation was added elsewhere. */
  refreshKey?: number;
  canReverse: boolean;
  onChanged?: () => void;
}

/** W3-E: where a credit (receipt, credit note, advance, debit note) has been applied, with a reverse action per row. */
const AllocationsPanel: React.FC<Props> = ({ creditType, creditId, refreshKey = 0, canReverse, onChanged }) => {
  const toast = useToast();
  const [rows, setRows] = useState<Allocation[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    Promise.resolve(AllocationService.forCredit(creditType, creditId))
      .then(r => setRows(Array.isArray(r) ? r : []))
      .catch(() => setRows([]));
  }, [creditType, creditId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const reverse = async (a: Allocation) => {
    setBusyId(a.id);
    try {
      await AllocationService.reverse(a.id);
      toast.showSuccess('Allocation reversed');
      load();
      onChanged?.();
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse allocation');
    } finally {
      setBusyId(null);
    }
  };

  if (rows.length === 0) return null;

  return (
    <Paper data-help-section="allocation.panel.section" sx={{ p: 2, mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Applied to</Typography>
      <ScrollableTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell data-help="allocation.panel.col.document">Document</TableCell>
              <TableCell data-help="allocation.panel.col.date">Date</TableCell>
              <TableCell data-help="allocation.panel.col.amount" align="right">Amount</TableCell>
              <TableCell data-help="allocation.panel.col.status">Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(a => (
              <TableRow key={a.id}>
                <TableCell>{a.targetNumber ?? a.targetId}</TableCell>
                <TableCell>{formatDate(a.allocationDate)}</TableCell>
                <TableCell align="right">{formatMoney(a.amount)}</TableCell>
                <TableCell>
                  <Chip size="small" label={humanizeEnum(a.status)} color={a.status === 'ACTIVE' ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  {canReverse && a.status === 'ACTIVE' && (
                    <Button data-help="allocation.panel.action.reverse" size="small" color="error" disabled={busyId === a.id} onClick={() => reverse(a)}>
                      Reverse
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScrollableTable>
      <Box />
    </Paper>
  );
};

export default AllocationsPanel;
